import {DataTable, Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow, DataTableSkeleton, DataTableHeader} from '@carbon/react';
import {useContext, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {NetworkContext} from '../lib/context';
import getFormatter from '../../../lib/formatter';

const DisruptionAnalysisSpecialtySummaryTable = observer(() => {
    const networkOptimizationStore = useContext(NetworkContext);
    const [loading, setLoading] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<Array<{id:string,cells: Array<{id:string, value:any, width?: string, type?: string}>}>>([]);

    const headers: DataTableHeader[] = [
        {key: 'specialty_code', header: 'Specialty Code'},
        {key: 'specialty', header: 'Specialty'},
        {key: 'members', header: 'Members'},
        {key: 'provider_locations', header: 'Provider Locations'},
        {key: 'member_impact', header: '% Member Impact'},
        {key: 'visits', header: 'Visits'},
        {key: 'utilization', header: '% Utilization'},
        {key: 'allowed', header: 'Allowed ($)'},
        {key: 'allowed_impact', header: '% Allowed Impact'},
    ];

    useEffect(() => {
        const controller = new AbortController();
        const loadData = async () => {
            setError(null);
            setLoading((loading)=>loading + 1); 
            try {
                await networkOptimizationStore.loadDisruptionAnalysisSpecialtiesData(controller);
            } catch (err) {
                setError('Failed to load estimated compliance summary data. Please try again later.');
            } finally {
                setLoading((loading)=>loading - 1); 
            }
        };

        loadData();
        return () => controller.abort();
    }, [networkOptimizationStore.appliedDisruptionAnalysisFilter, 
        networkOptimizationStore.runId, 
        networkOptimizationStore.revisionId, 
        networkOptimizationStore.readyToLoadData()]);


    useEffect(() => {
        setRows(networkOptimizationStore.disruptionAnalysisComplianceScores.map((score, index) => ({
            id: index.toString(),
            cells: [
                {id: 'specialty_code', value: score['specialty_code'], width: '10%'},
                {id: 'specialty', value: score['Specialty'], width: '20%'},
                {id: 'members', value: score['Members'], width: '10%'},
                {id: 'provider_locations', value: score['ProviderLocations'], width: '10%'},
                {id: 'member_impact', value: score['%MemberImpact'], type: 'percentfloor', width: '10%'},
                {id: 'visits', value: score['Visits'], width: '10%'},
                {id: 'utilization', value: score['%Utilization'], type: 'percentfloor', width: '10%'},
                {id: 'allowed', value: score['Allowed($)'], type: 'currency', width: '10%'},
                {id: 'allowed_impact', value: score['%AllowedImpact'], type: 'percentfloor', width: '10%'}
            ],
        })));
    }, [networkOptimizationStore.disruptionAnalysisComplianceScores]);

    if (!networkOptimizationStore.readyToLoadData()) {
        return null;
    }

    if (error) {
        return <p className="text-red-500">{error}</p>;
    }
    return (
        <div className="compliance-summary-page bg-white p-4 w-full">
            <h3 className="text-xl font-bold mb-4">Estimated Compliance Summary</h3>
            {loading ? <DataTableSkeleton 
                headers={headers} 
                columnCount={headers.length}
                rowCount={12}
                showHeader={false}
                showToolbar={false}
            /> : <TableContainer className="max-h-[70vh] overflow-x-auto" data-testid="disruption-analysis-compliance-summary-table-container">
                <DataTable rows={rows} headers={headers}>
                    {({headers}) => (
                        <Table className="min-w-full">
                            <TableHead>
                                <TableRow className="sticky table top-0 bg-white z-10">
                                    {headers.map(header => (
                                        <TableHeader
                                            key={header.key}
                                            className={`bg-white z-20 border-b border-gray-300 text-left p-2`}
                                            style={{width: `${header.key === 'specialty' ? '20%' : `${80 / (headers.length - 1)}%`}`}}
                                        >
                                            {header.header}
                                        </TableHeader>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody className="block max-h-[60vh] overflow-y-auto">
                                {rows.length === 0 ? (
                                    <TableRow className="table w-full">
                                        <TableCell 
                                            colSpan={headers.length} 
                                            className="p-3"
                                            style={{textAlign: 'center', fontSize: '1rem', fontWeight: '500'}}
                                        >
                                            No Data Available
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map(row => (
                                        <TableRow key={row.id} className="table w-full">
                                            {row.cells.map((cell) => {
                                                const formatter = cell?.type ? getFormatter(cell.type, .1) : {format: (value: any) => value};
                                                return (
                                                    <TableCell
                                                        key={cell.id}
                                                        className={`p-2`}
                                                        style={{width: cell.width}}
                                                    >
                                                        {formatter.format(cell.value)}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </DataTable>
            </TableContainer>}
        </div>
    );
});

export default DisruptionAnalysisSpecialtySummaryTable;
